import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const STATS = [
  {
    value: 1900,
    prefix: "$",
    suffix: " / year",
    label:
      "Typical saving for an existing Victorian home going fully electric (Victorian Government)",
  },
  {
    value: 2230,
    prefix: "$",
    suffix: " / year",
    label: "With rooftop solar (Victorian Government)",
  },
  {
    value: 400,
    prefix: "~$",
    suffix: " / year",
    label: "From switching gas hot water to a heat pump (Solar Victoria)",
  },
];

const CountUp = ({ value, prefix, suffix, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduceMotion) {
      setDisplay(value);
      return;
    }
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, reduceMotion]);

  return (
    <span ref={ref} data-testid={testid}>
      {prefix}
      {display.toLocaleString("en-AU")}
      {suffix}
    </span>
  );
};

const Savings = () => {
  return (
    <section
      data-testid="savings-section"
      className="bg-white py-16 md:py-24 border-b border-navy/10"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gdark mb-4 flex items-center justify-center gap-2">
            <span className="inline-block w-8 h-px bg-gdark" />
            What switching is worth
            <span className="inline-block w-8 h-px bg-gdark" />
          </p>
          <h2 className="font-serif font-black text-navy tracking-tight text-4xl md:text-5xl lg:text-6xl leading-tight">
            Real numbers, not promises.
          </h2>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 md:gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={s.value}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              data-testid={`savings-stat-${i}`}
              className="bg-cream rounded-[20px] p-8"
            >
              <p className="font-serif font-black text-gdark tracking-tight text-4xl md:text-5xl leading-none">
                <CountUp
                  value={s.value}
                  prefix={s.prefix}
                  suffix={s.suffix}
                  testid={`savings-number-${i}`}
                />
              </p>
              <p className="mt-4 text-base text-navy-muted leading-relaxed">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.2 }}
          data-testid="savings-tagline"
          className="mt-10 text-lg md:text-xl font-medium text-navy"
        >
          Switching your hot water is one of the biggest steps toward an
          all-electric home.
        </motion.p>

        <p
          data-testid="savings-sources"
          className="mt-4 text-xs text-navy-muted/80 space-x-4"
        >
          <span>Sources:</span>
          <a
            href="https://www.energy.vic.gov.au/households/save-with-all-electric-home"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="savings-source-energy-vic"
            className="underline underline-offset-2 hover:text-navy transition-colors duration-200"
          >
            energy.vic.gov.au
          </a>
          <a
            href="https://www.solar.vic.gov.au/why-choose-heat-pump-hot-water-system"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="savings-source-solar-vic"
            className="underline underline-offset-2 hover:text-navy transition-colors duration-200"
          >
            solar.vic.gov.au
          </a>
        </p>
      </div>
    </section>
  );
};

export default Savings;
